/**
 * ImageOrCodeSection.js
 * Section Image Docker + IDE optionnel avec régénération LOCK zones.
 */
import React from "react";
import {Box, Button, Chip, Paper, Stack, TextField, Tooltip, Typography} from "@mui/material";
import CodeEditorIDE from "./CodeEditorIDE";
import ApiAnalyticsEngineCtrl from "../../../../../api/ApiAnalyticsEngineCtrl";

const LANGUAGES = ["PYTHON", "R"];

const DEFAULT_REQUIREMENTS = "numpy\nscipy\npandas\nscikit-learn\n";

/** Génère la valeur par défaut Python pour un param, avec cast selon le type */
function pyParamLine(p) {
    const def = p.default != null ? p.default : p.min != null ? p.min : null;
    const pyDefault = def === null ? "None" : JSON.stringify(def);
    const raw = `params.get("${p.name}", ${pyDefault})`;
    const type = (p.type || "").toUpperCase();
    if (type === "INTEGER") return `    ${p.name} = int(${raw})`;
    if (type === "FLOAT" || type === "DOUBLE" || type === "NUMBER") return `    ${p.name} = float(${raw})`;
    if (type === "BOOLEAN") return `    ${p.name} = bool(${raw})`;
    if (type === "STRING") return `    ${p.name} = str(${raw})`;
    return `    ${p.name} = ${raw}`;
}

/** Génère la valeur par défaut R pour un param, avec cast selon le type */
function rParamLine(p) {
    const def = p.default != null ? p.default : p.min != null ? p.min : null;
    const rDefault = def === null ? "NULL" : JSON.stringify(def);
    const raw = `if (!is.null(params[["${p.name}"]])) params[["${p.name}"]] else ${rDefault}`;
    const type = (p.type || "").toUpperCase();
    if (type === "INTEGER") return `    ${p.name} <- as.integer(${raw})`;
    if (type === "FLOAT" || type === "DOUBLE" || type === "NUMBER") return `    ${p.name} <- as.numeric(${raw})`;
    if (type === "BOOLEAN") return `    ${p.name} <- as.logical(${raw})`;
    if (type === "STRING") return `    ${p.name} <- as.character(${raw})`;
    return `    ${p.name} <- ${raw}`;
}

const PYTHON_HANDLER_TEMPLATE = (series, outputs, params) => {
    const args = (series || []).map(s => s.name || "observed").join(", ");
    const ret = (outputs || []).map(o => `"${o.name || "value"}": ${o.name || "value"}`).join(", ");
    const paramDefaults = (params || []).map(p => pyParamLine(p)).join("\n");
    const inits = (outputs || []).map(o => `    ${o.name || "value"} = None`).join("\n");
    return `# --- LOCK:IMPORTS:START ---
import numpy as np
import warnings
warnings.filterwarnings("ignore")
# --- LOCK:IMPORTS:END ---

# --- LOCK:PARAMS:START ---
def handle(${args}, params=None):
    if params is None:
        params = {}
${paramDefaults || "    pass"}
${inits}
# --- LOCK:PARAMS:END ---

    # ── Votre code ici ──


# --- LOCK:RETURN:START ---
    return {${ret}}
# --- LOCK:RETURN:END ---
`;
};

const R_HANDLER_TEMPLATE = (series, outputs, params) => {
    const args = (series || []).map(s => s.name || "observed").join(", ");
    const ret = (outputs || []).map(o => `${o.name || "value"} = ${o.name || "value"}`).join(", ");
    const paramDefaults = (params || []).map(p => rParamLine(p)).join("\n");
    const inits = (outputs || []).map(o => `    ${o.name || "value"} <- NULL`).join("\n");
    return `# --- LOCK:IMPORTS:START ---
library(jsonlite)
# --- LOCK:IMPORTS:END ---

# --- LOCK:PARAMS:START ---
handle <- function(${args}, params = NULL) {
    if (is.null(params)) params <- list()
${paramDefaults || "    # pas de parametres"}
${inits}
# --- LOCK:PARAMS:END ---

    # Votre code ici


# --- LOCK:RETURN:START ---
    list(${ret})
}
# --- LOCK:RETURN:END ---
`;
};

function regenerateLockedZones(content, language, series, outputs, params) {
    if (!content) return content;
    const seriesArgs = (series || []).map(s => s.name || "observed");
    const outputKeys = (outputs || []).map(o => o.name || "value");
    const isPython = language === "PYTHON";
    const paramLines = (params || []).map(p => isPython ? pyParamLine(p) : rParamLine(p));
    const outputInits = isPython
        ? outputKeys.map(k => `    ${k} = None`)
        : outputKeys.map(k => `    ${k} <- NULL`);
    const zoneContent = {};
    if (isPython) {
        zoneContent["PARAMS"] = [`def handle(${seriesArgs.join(", ")}, params=None):`, "    if params is None:", "        params = {}",
            ...(paramLines.length > 0 ? paramLines : ["    pass"]),
            ...outputInits];
        zoneContent["RETURN"] = [`    return {${outputKeys.map(k => `"${k}": ${k}`).join(", ")}}`];
    } else {
        zoneContent["PARAMS"] = [`handle <- function(${seriesArgs.join(", ")}, params = NULL) {`, "    if (is.null(params)) params <- list()",
            ...(paramLines.length > 0 ? paramLines : ["    # pas de parametres"]),
            ...outputInits];
        zoneContent["RETURN"] = [`    list(${outputKeys.map(k => `${k} = ${k}`).join(", ")})`, "}"];
    }
    const lines = content.split("\n");
    const result = [];
    let skipUntilEnd = null;
    for (const line of lines) {
        const startMatch = line.match(/LOCK:(\w+):START/);
        if (startMatch) {
            result.push(line);
            if (zoneContent[startMatch[1]]) { result.push(...zoneContent[startMatch[1]]); skipUntilEnd = startMatch[1]; }
            continue;
        }
        if (skipUntilEnd) {
            const endMatch = line.match(/LOCK:(\w+):END/);
            if (endMatch && endMatch[1] === skipUntilEnd) { result.push(line); skipUntilEnd = null; }
            continue;
        }
        result.push(line);
    }
    return result.join("\n");
}

export default function ImageOrCodeSection({form, setField, versions = [], onSwitchVersion, isEdit = false}) {
    // IDE ouvert par défaut — option pour basculer en "image existante"
    const [mode, setMode] = React.useState("code"); // "code" | "image-only"
    const [language, setLanguageState] = React.useState("PYTHON");
    const [files, setFilesState] = React.useState({});
    // Fichiers plateforme (connector.py / connector.R) — lecture seule
    const [platformFiles, setPlatformFiles] = React.useState({});

    // Charger les fichiers plateforme (connector.py / connector.R) pour le langage courant
    React.useEffect(() => {
        let cancelled = false;
        ApiAnalyticsEngineCtrl.getPlatformFiles(language).then(({data}) => {
            if (!cancelled && data) setPlatformFiles(data);
        });
        return () => { cancelled = true; };
    }, [language]);

    // Ref pour toujours avoir la valeur courante de form.image (évite les closures stale)
    const imageRef = React.useRef(form.image);
    React.useEffect(() => { imageRef.current = form.image; }, [form.image]);

    // Synchroniser les fichiers locaux quand form.image.files change (switch version, édition, nouvelle version)
    const formFilesKey = React.useMemo(() => {
        const img = typeof form.image === "object" ? form.image : null;
        if (!img?.files) return "";
        // Inclure un hash du contenu pour détecter les changements de contenu (pas juste les clés)
        const entries = Object.entries(img.files).sort(([a], [b]) => a.localeCompare(b));
        return JSON.stringify(entries.map(([k, v]) => [k, v?.length ?? 0]));
    }, [form.image]);

    React.useEffect(() => {
        const img = typeof form.image === "object" ? form.image : null;
        if (img?.files && Object.keys(img.files).length > 0) {
            setFilesState(img.files);
            if (img.language) setLanguageState(img.language);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formFilesKey]);

    function parseImageRef() {
        const cur = imageRef.current;
        if (typeof cur === "object" && cur) return {...cur};
        if (typeof cur === "string" && cur) {
            const m = cur.match(/^(.+?)\/(.+):(.+)$/);
            return m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: cur, tag: ""};
        }
        return {};
    }

    function setFiles(updater) {
        setFilesState(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            const img = parseImageRef();
            img.files = next; img.language = language;
            setField("image", img);
            return next;
        });
    }
    function setLanguage(lang) {
        setLanguageState(lang);
        if (Object.keys(files).length > 0) {
            const img = parseImageRef();
            img.language = lang; img.files = files; setField("image", img);
        }
    }

    const depsKey = JSON.stringify({
        series: (form.series || []).map(s => s.name), outputs: (form.outputs || []).map(o => o.name),
        params: (form.params || []).map(p => ({name: p.name, default: p.default, type: p.type, min: p.min})),
        n: (form.series || []).length + (form.outputs || []).length + (form.params || []).length,
    });
    // Régénérer les zones LOCK quand series/outputs/params changent OU au premier chargement des fichiers
    const filesLoadedRef = React.useRef(false);
    React.useEffect(() => {
        if (Object.keys(files).length === 0) return;
        const ext = language === "R" ? ".R" : ".py";
        const hf = `handler${ext}`;
        if (!files[hf]) return;
        const updated = regenerateLockedZones(files[hf], language, form.series, form.outputs, form.params);
        if (updated !== files[hf]) setFiles(prev => ({...prev, [hf]: updated}));
        filesLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depsKey, Object.keys(files).length]);

    function generateFromScratch() {
        const ext = language === "R" ? ".R" : ".py";
        const handler = language === "PYTHON"
            ? PYTHON_HANDLER_TEMPLATE(form.series, form.outputs, form.params)
            : R_HANDLER_TEMPLATE(form.series, form.outputs, form.params);
        const newFiles = {
            [`handler${ext}`]: handler,
            ...(language === "PYTHON" ? {"requirements.txt": DEFAULT_REQUIREMENTS} : {"install_packages.R": 'install.packages(c("jsonlite"))\n'}),
        };
        // Mettre à jour les fichiers locaux sans écraser registry/image/tag
        setFilesState(newFiles);
        // Enrichir l'image existante avec files + language
        const cur = imageRef.current;
        let img;
        if (typeof cur === "object" && cur) {
            img = {...cur};
        } else if (typeof cur === "string" && cur) {
            // Parser la string "registry/image:tag" en objet
            const m = cur.match(/^(.+?)\/(.+):(.+)$/);
            img = m ? {registry: m[1], image: m[2], tag: m[3]} : {registry: "", image: cur, tag: ""};
        } else {
            img = {};
        }
        img.files = newFiles;
        img.language = language;
        setField("image", img);
    }

    const imageStr = typeof form.image === "string" ? form.image
        : (form.image ? `${form.image.registry || ""}/${form.image.image || ""}:${form.image.tag || ""}` : "");

    function handleImageChange(e) {
        const raw = e.target.value;
        const m = raw.match(/^(.+?)\/(.+):(.+)$/);
        if (m) setField("image", {registry: m[1], image: m[2], tag: m[3]});
        else setField("image", raw);
    }

    const currentVersion = form.version || (typeof form.image === "object" ? form.image?.tag : "") || "";

    // Auto-charger le template à l'ouverture si pas de fichiers
    React.useEffect(() => {
        if (mode === "code" && Object.keys(files).length === 0) {
            const img = typeof form.image === "object" ? form.image : null;
            if (img?.files && Object.keys(img.files).length > 0) {
                // Fichiers existants en base → les restaurer
                setFilesState(img.files);
                if (img.language) setLanguageState(img.language);
            } else if (!isEdit) {
                // Nouveau modèle sans fichiers → générer le template
                generateFromScratch();
            }
            // En édition sans fichiers → ne rien faire, les fichiers arriveront via formFilesKey
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, isEdit]);

    return (
        <Stack spacing={2} sx={{flex: 1, minHeight: 0}}>
            {/* Barre du haut : version + historique + mode + langage */}
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                {currentVersion && (
                    <Chip label={`v${currentVersion}`} size="small" color="primary" variant="outlined"/>
                )}
                <Box sx={{flex: 1}}/>

                {/* Toggle mode */}
                <Tooltip title={mode === "code"
                    ? "Basculer en mode image existante (pas de code, juste le deploiement d'une image Docker)"
                    : "Basculer en mode editeur de code pour creer le moteur"} placement="top" arrow>
                    <Chip label={mode === "code" ? "Editeur de code" : "Image existante"}
                          variant="outlined"
                          color={mode === "code" ? "secondary" : "default"}
                          onClick={() => setMode(mode === "code" ? "image-only" : "code")}
                          sx={{cursor: "pointer", fontSize: 11}}/>
                </Tooltip>

                {mode === "code" && (
                    <TextField select label="Langage" size="small" sx={{width: 120}}
                               value={language} onChange={e => setLanguage(e.target.value)}
                               slotProps={{select: {native: true}}}>
                        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </TextField>
                )}
            </Stack>

            {/* Mode image existante */}
            {mode === "image-only" && (
                <Paper sx={{p: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 2}}>
                    <Typography color="text.secondary" variant="body2">
                        Pas de code source — l'image Docker definie dans l'identite sera utilisee directement pour le deploiement.
                    </Typography>
                </Paper>
            )}

            {/* Mode éditeur de code */}
            {mode === "code" && (
                <Stack spacing={1.5} sx={{flex: 1, minHeight: 0}}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Tooltip title="Regenerer le template depuis les series/outputs/params. Le code utilisateur sera ecrase." placement="top" arrow>
                            <Button variant="outlined" size="small" color="warning" onClick={generateFromScratch} sx={{fontSize: 11}}>
                                Reinitialiser le template
                            </Button>
                        </Tooltip>
                    </Stack>
                    <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
                        {isEdit && Object.keys(files).length === 0 ? (
                            <Paper sx={{p: 3, textAlign: "center", bgcolor: "action.hover", borderRadius: 2, flex: 1, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                <Typography color="text.secondary" variant="body2">
                                    Chargement du code source...
                                </Typography>
                            </Paper>
                        ) : (
                            <CodeEditorIDE files={files} setFiles={setFiles} language={language}
                                           readOnlyFiles={platformFiles}/>
                        )}
                    </Box>
                </Stack>
            )}
        </Stack>
    );
}
